import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { CONSENT_DENIED_TTL_DAYS, disableGoogleAnalytics, loadGoogleAnalytics } from './web-analytics';

export type AnalyticsConsent = 'granted' | 'denied';

/** Distinct de `ls_consent` (opt-in newsletter, cf. page Confidentialité). */
export const ANALYTICS_CONSENT_KEY = 'ls_analytics_consent';

/**
 * Consentement aux statistiques détaillées (Google Analytics 4).
 *
 * Cloudflare Web Analytics n'en a pas besoin (sans cookie) ; GA4, si : il ne se charge
 * qu'après « Accepter ». Sans `googleAnalyticsId` (ou avec `features.analytics` à faux),
 * il n'y a rien à consentir : le bandeau ne s'affiche jamais.
 *
 * Le choix est lu dans `restore()`, appelé après le premier rendu navigateur
 * (`afterNextRender` du bandeau) : le serveur ne connaît pas le localStorage, lire plus tôt
 * ferait diverger le rendu SSR et l'hydratation.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsConsentService {
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly measurementId = environment.features.analytics ? environment.googleAnalyticsId : '';

  private readonly restored = signal(false);
  private readonly choice = signal<AnalyticsConsent | null>(null);

  /** Vrai si GA est configuré pour cet environnement. */
  readonly enabled = this.isBrowser && !!this.measurementId;
  readonly consent = this.choice.asReadonly();
  readonly bannerVisible = computed(() => this.enabled && this.restored() && this.choice() === null);

  /** Relit le choix mémorisé et charge GA s'il avait été accepté. */
  restore(): void {
    if (!this.enabled || this.restored()) return;
    const saved = this.readStorage();
    this.choice.set(saved);
    this.restored.set(true);
    if (saved === 'granted') loadGoogleAnalytics(this.document, this.measurementId);
  }

  accept(): void {
    if (!this.enabled) return;
    this.save('granted');
    loadGoogleAnalytics(this.document, this.measurementId);
  }

  refuse(): void {
    if (!this.enabled) return;
    this.save('denied');
    disableGoogleAnalytics(this.document, this.measurementId);
  }

  /** « Gérer les cookies » : rouvre le bandeau ; le choix précédent reste appliqué jusqu'au nouveau. */
  reopen(): void {
    if (!this.enabled) return;
    this.restored.set(true);
    this.choice.set(null);
  }

  private save(value: AnalyticsConsent): void {
    this.choice.set(value);
    this.restored.set(true);
    try {
      localStorage.setItem(ANALYTICS_CONSENT_KEY, JSON.stringify({ choice: value, at: Date.now() }));
    } catch {
      // Stockage indisponible (navigation privée, données bloquées) : choix valable pour la visite.
    }
  }

  /** Choix mémorisé, ou null s'il est absent, illisible (ancien format compris) ou, pour un refus seulement, plus vieux que `CONSENT_DENIED_TTL_DAYS`. */
  private readStorage(): AnalyticsConsent | null {
    try {
      const raw = localStorage.getItem(ANALYTICS_CONSENT_KEY);
      if (!raw) return null;
      const saved = JSON.parse(raw) as { choice?: unknown; at?: unknown };
      const valid = saved.choice === 'granted' || saved.choice === 'denied';
      if (typeof saved.at !== 'number') return null;
      const fresh = saved.choice === 'granted' || Date.now() - saved.at < CONSENT_DENIED_TTL_DAYS * 86_400_000;
      return valid && fresh ? (saved.choice as AnalyticsConsent) : null;
    } catch {
      return null;
    }
  }
}
